import { FC } from 'react'

import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber'
import EventIcon from '@mui/icons-material/Event'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import { Box, Typography } from '@mui/material'

import { useGetInfoCardsQuery } from 'services/dashboard-analytics/api'
import { DashboardAnalyticsFilter } from 'services/dashboard-analytics/types'

import InfoCard from './InfoCard'

interface InfoCardsProps {
  filters: DashboardAnalyticsFilter[]
}

const InfoCards: FC<InfoCardsProps> = ({ filters }) => {
  const mapFilters = (filters: DashboardAnalyticsFilter[]): DashboardAnalyticsFilter[] => {
    const filterMapping: { [key: string]: string } = {
      l_booking_id: 'booking_id',
      listing_category_name: 'category_name',
      listing_event_name: 'event_name',
      l_event_date: 'event_date',
      listing_event_group_name: 'event_group_name',
      order_id: 'su_order_id',
      platform_name: 'su_platform_name',
    }

    return filters.map((filter) => ({
      ...filter,
      column: filterMapping[filter.column] || filter.column,
    }))
  }

  const mappedFilters = mapFilters(filters)

  const { data: cardsData } = useGetInfoCardsQuery(
    {
      filters: mappedFilters,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  )

  const isProfitable = cardsData?.profit?.profitPercent && cardsData?.profit?.profitPercent > 0

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateAreas: {
          xs: `
            'orders'
            'tickets'
            'revenue'
            'events'
            'totalProfit'
            `,
          md: `
          'orders tickets'
          'revenue events'
          'totalProfit totalProfit'
          'totalProfit totalProfit'
          `,
          lg: `
            'orders orders orders tickets tickets tickets totalProfit totalProfit totalProfit totalProfit'
            'events events events revenue revenue revenue totalProfit totalProfit totalProfit totalProfit'
            `,
          xl: `
          'orders tickets revenue events totalProfit'
        `,
        },
        gap: '24px',
        p: '0px',
      }}
    >
      <InfoCard
        title="Orders"
        mainValue={cardsData?.orders?.total || 0}
        icon={<ShoppingCartIcon />}
        iconColor="#2196f3"
        iconBgColor="#e3f2fd"
        wrapperSx={{ gridArea: 'orders' }}
      >
        <Typography variant="body2" color="success.main" sx={{ fontSize: '16px', fontWeight: 500 }}>
          {cardsData?.orders?.newToday || 0} new
          <Typography
            component="span"
            sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}
            color="text.secondary"
          >
            {' '}
            for today
          </Typography>
        </Typography>
        <Typography variant="body2" color="error.main" sx={{ fontSize: '16px', fontWeight: 500 }}>
          {cardsData?.orders?.unsent || 0} unsent
        </Typography>
      </InfoCard>

      <InfoCard
        title="Tickets"
        mainValue={cardsData?.tickets?.total || 0}
        icon={<ConfirmationNumberIcon />}
        iconColor="#64b5f6"
        iconBgColor="#e1f5fe"
        wrapperSx={{ gridArea: 'tickets' }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: '16px', fontWeight: 500 }}
        >
          <Typography
            component="span"
            sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}
            color="success.main"
          >
            {' '}
            {cardsData?.tickets?.sold || 0}
          </Typography>
          /
          <Typography
            component="span"
            sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}
            color="error.main"
          >
            {cardsData?.tickets?.unsold || 0}
          </Typography>
          /
          <Typography
            component="span"
            sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}
            color="warning.main"
          >
            {cardsData?.tickets?.notListed || 0}{' '}
          </Typography>
        </Typography>
        <Typography
          component="span"
          sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}
          color="text.secondary"
        >
          Sold/Unsold/Not listed
        </Typography>
      </InfoCard>

      <InfoCard
        title="Revenue"
        mainValue={cardsData?.revenue?.amount || 0}
        icon={<AttachMoneyIcon />}
        iconColor="#ff9800"
        iconBgColor="#fff3e0"
        wrapperSx={{ gridArea: 'revenue' }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: '16px',
            fontWeight: 500,
            color:
              (cardsData?.revenue?.percentChangeSinceLastWeek || 0) < 0
                ? 'error.main'
                : 'success.main',
          }}
        >
          {cardsData?.revenue?.percentChangeSinceLastWeek || 0}%{' '}
          <Typography
            component="span"
            sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}
            color="text.secondary"
          >
            since last week
          </Typography>
        </Typography>
      </InfoCard>

      <InfoCard
        title="Total Profit"
        mainValue={cardsData?.profit?.profit || 0}
        icon={<AttachMoneyIcon />}
        iconColor={isProfitable ? '#00bb1f' : '#eb1919'}
        iconBgColor={isProfitable ? '#c9f3be' : '#ffcfcf'}
        wrapperSx={{ gridArea: 'totalProfit' }}
      >
        <Typography
          variant="body2"
          color={isProfitable ? 'success.main' : 'error.main'}
          sx={{ fontSize: '16px', fontWeight: 500 }}
        >
          {cardsData?.profit?.profitPercent || 0}%
        </Typography>
      </InfoCard>

      <InfoCard
        title="Events"
        mainValue={cardsData?.events?.total || 0}
        icon={<EventIcon />}
        iconColor="#9c27b0"
        iconBgColor="#f3e5f5"
        wrapperSx={{ gridArea: 'events' }}
      >
        <Typography variant="body2" color="success.main" sx={{ fontSize: '16px', fontWeight: 500 }}>
          {cardsData?.events?.left || 0}{' '}
          <Typography
            component="span"
            sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}
            color="text.secondary"
          >
            left
          </Typography>
        </Typography>
      </InfoCard>
    </Box>
  )
}

export default InfoCards
